<template>
  <div 
    class="loading-spinner"
    :class="[
      sizeClass,
      { 'loading-spinner-centered': centered }
    ]"
  >
    <div class="spinner-container">
      <div class="spinner" :class="colorClass"></div>
      <div v-if="message" class="loading-message">
        {{ message }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  size?: 'sm' | 'md' | 'lg'
  color?: 'primary' | 'secondary' | 'accent'
  message?: string
  centered?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  color: 'primary',
  message: '',
  centered: true
})

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'loading-spinner-sm'
    case 'lg':
      return 'loading-spinner-lg'
    default:
      return 'loading-spinner-md'
  }
})

const colorClass = computed(() => {
  switch (props.color) {
    case 'secondary':
      return 'spinner-secondary'
    case 'accent':
      return 'spinner-accent'
    default:
      return 'spinner-primary'
  }
})
</script>

<style scoped>
.loading-spinner {
  @apply flex items-center justify-center;
}

.loading-spinner-centered {
  @apply w-full h-full min-h-32;
}

.spinner-container {
  @apply flex flex-col items-center space-y-3;
}

.spinner {
  @apply border-4 border-gray-200 border-t-transparent rounded-full animate-spin;
}

.loading-spinner-sm .spinner {
  @apply w-6 h-6 border-2;
}

.loading-spinner-md .spinner {
  @apply w-8 h-8;
}

.loading-spinner-lg .spinner {
  @apply w-12 h-12 border-4;
}

.spinner-primary {
  @apply border-t-rogue-deep-forest;
}

.spinner-secondary {
  @apply border-t-rogue-vibrant-green;
}

.spinner-accent {
  @apply border-t-rogue-rich-coral;
}

.loading-message {
  @apply text-sm text-gray-600 font-medium;
}

/* Smooth animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Performance optimization */
.spinner {
  will-change: transform;
  contain: layout style paint;
}
</style>